import React, { ReactNode, useMemo, useRef, useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import * as Dropdown from 'src/shared/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import styles from './SelectMultiple.module.scss';

export type SelectMultipleOption = {
    value: string;
    label: ReactNode;
    isDisabled?: boolean;
};

export type SelectMultipleOptionGroup = {
    label: ReactNode;
    options: SelectMultipleOption[];
};

export type CommonSelectProps = {
    testId?: string;
    onChange: (value: string[]) => void;
    values: string[];
    label?: string;
    className?: string;
    width?: number | string;
    optionsWidth?: number | string;
    onClose?: () => void;
    onOpen?: () => void;
    noSelectionText?: string;
    showSelectAll?: boolean;
    showDeselectAll?: boolean;
    headerContent?: ReactNode;
    footerContent?: ReactNode;
    disabled?: boolean;
    formatSelectedValues?: (count: number) => string;
};

export type OptionsOnly = { options: SelectMultipleOption[]; optionGroups?: undefined };
export type GroupsOnly = { optionGroups: SelectMultipleOptionGroup[]; options?: undefined };

export type SelectProps = (OptionsOnly | GroupsOnly) & CommonSelectProps;

const SelectMultiple = (props: SelectProps) => {
    const {
        options,
        optionGroups,
        onChange,
        values,
        label,
        width = 150,
        optionsWidth,
        onClose,
        onOpen,
        noSelectionText = 'None selected',
        showSelectAll = true,
        showDeselectAll = false,
        headerContent,
        footerContent,
        disabled = false,
        testId = 'select-multiple',
        formatSelectedValues,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [contentMinWidth, setContentMinWidth] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        if (triggerRef.current) {
            setContentMinWidth(triggerRef.current.getBoundingClientRect().width);
        }
    }, [width]);

    const handleToggle = (value: string) => {
        const isSelected = values.includes(value);
        const next = isSelected ? values.filter(v => v !== value) : [...values, value];
        onChange(next);
    };

    const handleClose = () => {
        setIsOpen(false);
        if (onClose) {
            onClose();
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        if (onOpen) {
            onOpen();
        }
    };

    const flatOptions: SelectMultipleOption[] = useMemo(() => {
        if (optionGroups && optionGroups.length) {
            return optionGroups.flatMap(group => group.options);
        }
        if (options && options.length) {
            return options;
        }
        return [];
    }, [optionGroups, options]);

    const containerClassNames = classNames(styles.selectContainer, props.className);
    const selectAllClassNames = classNames(styles.selectAll, {
        [styles.deselectAllDisabled]: values.length === flatOptions.length,
    });
    const deselectAllClassNames = classNames(styles.deselectAll, {
        [styles.deselectAllDisabled]: !values.length,
    });

    const triggerLabel = !values.length
        ? noSelectionText
        : formatSelectedValues
        ? formatSelectedValues(values.length)
        : `${values.length} ${values.length > 1 ? 'values' : 'value'} selected`;

    return (
        <div className={containerClassNames}>
            {label && <label className='block text-sm font-medium mb-1'>{label}</label>}
            <Dropdown.DropdownMenu open={isOpen} onOpenChange={open => (open ? handleOpen() : handleClose())}>
                <Dropdown.DropdownMenuTrigger asChild>
                    <button
                        type='button'
                        className='flex h-10 w-[var(--select-width,auto)] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                        style={{ ['--select-width' as any]: typeof width === 'number' ? `${width}px` : width }}
                        data-testid={testId}
                        disabled={disabled}
                        ref={triggerRef}
                        onClick={() => {
                            handleOpen();
                        }}
                        aria-expanded={isOpen}
                    >
                        <span className='truncate'>{triggerLabel}</span>
                        <ChevronDown className='h-4 w-4 shrink-0 opacity-50 ml-2' />
                    </button>
                </Dropdown.DropdownMenuTrigger>
                <Dropdown.DropdownMenuContent className={styles.list} sideOffset={4} style={{ minWidth: contentMinWidth }}>
                    {headerContent && <div className={styles.headerContent}>{headerContent}</div>}
                    {optionGroups && optionGroups.length
                        ? optionGroups
                              .map((group, groupIndex) => [
                                  <div key={`header-${groupIndex}`} className={styles.groupHeader}>
                                      <span className={styles.groupHeaderLabel}>{group.label}</span>
                                      <span className={styles.groupHeaderLine} />
                                  </div>,
                                  ...group.options.map((option, index) => (
                                      <Dropdown.DropdownMenuCheckboxItem
                                          key={`g${groupIndex}-o${index}`}
                                          checked={values.includes(option.value)}
                                          disabled={option.isDisabled}
                                          onCheckedChange={() => handleToggle(option.value)}
                                          onSelect={e => e.preventDefault()}
                                          className='flex items-center gap-2'
                                          style={{ width: optionsWidth }}
                                      >
                                          {option.label}
                                      </Dropdown.DropdownMenuCheckboxItem>
                                  )),
                              ])
                              .flat()
                        : (options || []).map((option, index) => (
                              <Dropdown.DropdownMenuCheckboxItem
                                  key={index}
                                  checked={values.includes(option.value)}
                                  disabled={option.isDisabled}
                                  onCheckedChange={() => handleToggle(option.value)}
                                  onSelect={e => e.preventDefault()}
                                  className='flex items-center gap-2'
                                  style={{ width: optionsWidth }}
                              >
                                  {option.label}
                              </Dropdown.DropdownMenuCheckboxItem>
                          ))}
                    {(showSelectAll || showDeselectAll || footerContent) && (
                        <div className={styles.selectFooter}>
                            {showSelectAll && (
                                <span className={selectAllClassNames} onClick={() => onChange(flatOptions.map(option => option.value))}>
                                    Select all
                                </span>
                            )}
                            {showDeselectAll && (
                                <span className={deselectAllClassNames} onClick={() => onChange([])}>
                                    Deselect all
                                </span>
                            )}
                            {footerContent && <span className={styles.footerContent}>{footerContent}</span>}
                        </div>
                    )}
                </Dropdown.DropdownMenuContent>
            </Dropdown.DropdownMenu>
        </div>
    );
};

export default SelectMultiple;
